import React, {
  useCallback, useMemo, useContext, memo, useState, useEffect,
} from 'react';
import _ from 'underscore';
import {
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
  Tooltip,
  ToggleButton,
} from '@fluentui/react-components';
import {
  ThumbDislike16Regular,
  ThumbLike16Regular,
  ThumbDislike16Filled,
  ThumbLike16Filled,
  bundleIcon,
} from '@fluentui/react-icons';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import './global';
import { FeedbackType } from './botTypes';

const ThumbLike = bundleIcon(ThumbLike16Filled, ThumbLike16Regular);
const ThumbDislike = bundleIcon(ThumbDislike16Filled, ThumbDislike16Regular);

export interface IFeedbackData {
  prevFeedback: FeedbackType;
  currentFeedback: FeedbackType;
}

type IFeedbackButtonsProps = {
  onFeedbackChange: (
    data: IFeedbackData
  ) => void;
  currentFeedback?: FeedbackType;
  ariaHiddenWhileLoading?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>;

const useFeedbackButtonsStyles = makeStyles({
  root: {
    display: 'flex',
  },
  buttonIcon: {
    color: tokens.colorNeutralForeground3,
    '&.fui-Icon-filled': {
      color: tokens.colorNeutralForeground2BrandSelected,
    },
  },
  button: {
    ...shorthands.padding(0),
  },
});

export const FeedbackButtons: React.FC<IFeedbackButtonsProps> = memo(({
  onFeedbackChange,
  className,
  currentFeedback: currentFeedbackFromParent,
  ariaHiddenWhileLoading,
  ...props
}) => {
  const styles = useFeedbackButtonsStyles();
  const [currentFeedback, setCurrentFeedback] = useState<FeedbackType>('None');
  const thumbsUpChecked = currentFeedback === 'ThumbsUp';
  const thumbsDownChecked = currentFeedback === 'ThumbsDown';

  const onPositiveClick = useCallback(() => {
    const newFeedback = currentFeedback === 'ThumbsUp' ? 'None' : 'ThumbsUp';
    onFeedbackChange({ prevFeedback: currentFeedback, currentFeedback: newFeedback });
    setCurrentFeedback(newFeedback);
  }, [currentFeedback, onFeedbackChange]);

  const onNegativeClick = useCallback(() => {
    const newFeedback = currentFeedback === 'ThumbsDown' ? 'None' : 'ThumbsDown';
    onFeedbackChange({ prevFeedback: currentFeedback, currentFeedback: newFeedback });
    setCurrentFeedback(newFeedback);
  }, [currentFeedback, onFeedbackChange]);

  const thumbLikeIcon = useMemo(() => <ThumbLike className={styles.buttonIcon} />, [styles.buttonIcon]);
  const thumbsDislikeIcon = useMemo(() => <ThumbDislike className={styles.buttonIcon} />, [styles.buttonIcon]);
  const i18n = useContext(I18nContext) as I18nModel;

  useEffect(() => {
    if (currentFeedbackFromParent != null && _.contains(['ThumbsUp', 'ThumbsDown'], currentFeedbackFromParent)) {
      setCurrentFeedback(currentFeedbackFromParent);
    }
  }, [currentFeedbackFromParent]);

  return (
    <div className={mergeClasses(styles.root, className)} {...props}>
      <Tooltip
        content={i18n.getString(_TL_('Like'))}
        relationship="label"
      >
        <ToggleButton
          checked={thumbsUpChecked}
          className={mergeClasses(styles.button, 'thumbsup-btn')}
          onClick={onPositiveClick}
          appearance="transparent"
          size="small"
          aria-label={i18n.getString(_TL_('thumbs up'))}
          icon={thumbLikeIcon}
          aria-hidden={ariaHiddenWhileLoading}
          tabIndex={ariaHiddenWhileLoading ? -1 : 0}
        />
      </Tooltip>
      <Tooltip
        content={i18n.getString(_TL_('Dislike'))}
        relationship="label"
      >
        <ToggleButton
          checked={thumbsDownChecked}
          className={mergeClasses(styles.button, 'thumbsdown-btn')}
          onClick={onNegativeClick}
          appearance="transparent"
          size="small"
          aria-label={i18n.getString(_TL_('thumbs down'))}
          icon={thumbsDislikeIcon}
          aria-hidden={ariaHiddenWhileLoading}
          tabIndex={ariaHiddenWhileLoading ? -1 : 0}
        />
      </Tooltip>
    </div>
  );
});
